/**
 * CentralIdField Component
 *
 * A specialized form field component for selecting Central IDs from a searchable dropdown.
 * Integrates with react-hook-form for form state management and validation.
 * Options are loaded through `/proxy-api?operation=centralId`.
 *
 * @component
 * @example
 * ```jsx
 * <CentralIdField
 *   control={formControl}
 *   trigger={triggerValidation}
 *   setValue={setFormValue}
 *   setError={setFormError}
 *   getValues={getFormValues}
 * />
 * ```
 *
 * @param {Object} props - Component props
 * @param {Object} [props.control=null] - React Hook Form control object for form state management
 * @param {Function} [props.trigger=() => {}] - React Hook Form trigger function to validate specific fields
 * @param {Function} [props.setValue=() => {}] - React Hook Form setValue function to update field values
 * @param {Function} [props.setError=() => {}] - React Hook Form setError function to set field errors
 * @param {Function} [props.getValues=() => {}] - React Hook Form getValues function to retrieve current field values
 * @param {boolean} [props.error=false] - Whether the field has a validation error
 * @param {string} [props.errorMessage=''] - Error message to display if validation fails
 * @param {Object} [props.restProps] - Additional props passed to ReactSelectFormField component
 *
 * @returns {JSX.Element} A form field with async search capability for Central IDs
 */

import ReactSelectFormField from './ReactSelectFormField'

const MAX_INT32 = 2147483647

export default function CentralIdField({
  control = null,
  trigger = () => {},
  setValue = () => {},
  setError = () => {},
  getValues = () => {},
  error = false,
  errorMessage = '',
  ...restProps
}) {
  return (
    <ReactSelectFormField
      asyncselect
      id='centralId_'
      name='centralId'
      label='Central ID'
      control={control}
      rules={{
        required: 'This field is required',
      }}
      placeholder='Type to search Central IDs'
      trigger={trigger}
      setValue={setValue}
      setError={setError}
      value={getValues('centralId')}
      skipDefaultApiResponseHandling
      apiOperationName='centralId'
      searchQueryKey='centralNameOrId'
      apiQuery={`size=${MAX_INT32}`}
      debounceDelay={1000}
      responseValueParser={resp => {
        if (resp?.message !== 'Unable to process proxy API request') {
          if (resp?.content && resp.content.length > 0)
            return resp.content.map(({ centralId: carID }) => ({ label: carID, value: carID }))
          else return []
        } else {
          setError('centralId', {
            type: 'manual',
            message: `Can't fetch options from server`,
          })
        }
      }}
      error={error}
      errorMessage={errorMessage}
      {...restProps}
    />
  )
}
